import { Sidebar, SidebarTrigger, useSidebar } from '@ibsys/kit';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/shared/ui';
import type { NavigationNode } from '../../model';
import { NavigationTree } from './NavigationTree';

type Props = {
  /** Дерево узлов навигации по атрибутам диалога. */
  nodes: NavigationNode[];
  /** Активный узел (синхронизирован со скроллом «портянки»). */
  selectedNodeId?: string | null;
  onNodeSelect: (nodeId: string) => void;
  /** Новый порядок верхнеуровневых узлов после перетаскивания. */
  onReorder: (nodeIds: string[]) => void;
};

/**
 * Сворачиваемая панель навигации по атрибутам.
 *
 * Свёрнутая панель оставляет только переключатель и отдаёт ширину значениям
 * атрибутов. Панель работает в собственном контексте сайдбара (`SidebarProvider`
 * в `DialogDataTab`), поэтому не управляет сайдбаром приложения и панелью транскрипта.
 */
export const NavigationSidebar: FC<Props> = ({ nodes, selectedNodeId, onNodeSelect, onReorder }) => {
  const { t } = useTranslation(['dialogs']);
  const { open } = useSidebar();

  // Переключатель назван действием, доступным сейчас: развёрнутое меню он сворачивает,
  // свёрнутое — разворачивает
  const toggleLabel = open ? t('dialogs:collapseAttributesNavigation') : t('dialogs:expandAttributesNavigation');

  return (
    <Sidebar
      collapsible="icon"
      // static — сайдбар кита по умолчанию `fixed` во весь экран, здесь же он живёт внутри карточки диалога
      className="static h-full"
      classNames={{ wrapper: 'h-full', inner: 'bg-transparent' }}
    >
      <div className="flex items-center p-2">
        <Tooltip tooltip={toggleLabel}>
          <SidebarTrigger aria-label={toggleLabel} />
        </Tooltip>
      </div>
      {/* Горизонтальные отступы у дерева: подсветка активного узла не должна упираться в границы меню.
          Цвет основного текста — на контейнере дерева: сайдбар кита приносит цвет текста навигации
          по системе, к содержимому карточки диалога не относящийся. Наследование перебивается здесь,
          а не на узлах, чтобы кит сам красил наведение и выбор — и у разворачиваемых узлов, и у листовых */}
      {open && (
        <div className="flex-1 overflow-auto px-2 pb-2 text-foreground">
          <NavigationTree
            nodes={nodes}
            onNodeSelect={onNodeSelect}
            selectedNodeId={selectedNodeId}
            onReorder={onReorder}
          />
        </div>
      )}
    </Sidebar>
  );
};
